import { Stack } from '@/components/layout';
import Text from '@/components/ui/text/text';
import { STRINGS } from '@/constants/strings';
import { useCoachQuery } from '@/features/home/queries';
import { createStyles, useScaling } from '@/hooks/use-scaling';
import { useSessionStore } from '@/stores/session-store';
import { colors } from '@/theme/colors';
import { AsYouType } from 'libphonenumber-js';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

// Content
export function InfoRow({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  return (
    <Stack style={styles.infoRow}>
      <Text
        fontFamily={isTablet ? 'Inter' : 'Montserrat'}
        fontSize={isTablet ? 11 : 10}
        fontWeight={isTablet ? '400' : '500'}
        transform="uppercase"
        style={styles.txtInfo}>
        {label}
      </Text>
      <Text
        variant={isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Bold 10px'}
        style={[styles.infoValue, { marginTop: scale(4) }]}>
        {value}
      </Text>
    </Stack>
  );
}

export function DetailCoachContent() {
  const styles = useStyles();
  const { isTablet } = useScaling();
  const uid = useSessionStore(useShallow(state => state.user?.uid));
  const { data: coach } = useCoachQuery(uid);

  const address: string = useMemo(() => {
    const street1 = coach?.address?.street1?.trim() ?? '';
    const street2 = coach?.address?.street2?.trim() ?? '';
    const city = coach?.address?.city?.trim() ?? '';
    const state = coach?.address?.state?.trim() ?? '';
    const zipcode = coach?.address?.zipcode?.trim() ?? '';

    const street = [street1, street2].filter(Boolean).join(' ');
    const cityState = city && state ? `${city}, ${state}` : city || state;
    const formatted = [street, [cityState, zipcode].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(' ');

    return formatted.trim() || STRINGS.common.noAddress;
  }, [coach?.address]);

  const normalizedPhone = coach?.phone?.startsWith('+') ? coach?.phone : `+1${coach?.phone}`;
  const formatter = new AsYouType();
  const formattedPhone = formatter.input(`${normalizedPhone}`);
  const phoneNumber = formattedPhone;

  return (
    <Stack flex={1} backgroundColor={'white'}>
      <Stack style={styles.section}>
        <Text
          fontWeight={isTablet ? undefined : '800'}
          variant={
            isTablet
              ? 'Font Inter/Subtitle/Bold 14px'
              : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
          }
          style={styles.sectionTitle}>
          {STRINGS.player.profile.overview.contactInfoTitle}
        </Text>
        <InfoRow
          label={STRINGS.coachProfile.content.phoneNumber}
          value={coach?.phone ? phoneNumber : STRINGS.common.noPhone}
        />
        <InfoRow label={STRINGS.coachProfile.content.email} value={coach?.email ?? ''} />
        <InfoRow label={STRINGS.coachProfile.content.address} value={address} />
      </Stack>
      {!isTablet && <Stack flex={1} backgroundColor={colors['2']} />}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  // Content
  txtInfo: {
    color: colors['6'],
  },
  section: {
    paddingHorizontal: scale(18),
    paddingVertical: scale(16),
    gap: scale(24),
  },
  sectionTitle: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  infoRow: {},
  infoValue: {
    color: colors.Blue,
  },
}));
